import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  DocumentTextIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import { guardianApi } from '../../services/api/guardian';
import { useGuardianAuthStore } from '../../stores/guardianAuthStore';
import { Card, H1, H3, Text, Badge, Button, Spinner, Alert } from '../../components/ui';
import { formatDate } from '../../utils/date';

export const GuardianAssessments: React.FC = () => {
  const { children: storeChildren } = useGuardianAuthStore();
  const [selectedChildId, setSelectedChildId] = useState<string | 'all'>('all');

  const { data: fetchedChildren } = useQuery({
    queryKey: ['guardian-children'],
    queryFn: guardianApi.portal.getChildren,
    enabled: !storeChildren || storeChildren.length === 0,
  });

  const childList = storeChildren && storeChildren.length > 0 ? storeChildren : fetchedChildren || [];

  const { data: assessments, isLoading, error } = useQuery({
    queryKey: ['guardian-all-assessments', selectedChildId, childList.map((c: any) => c.id)],
    queryFn: async () => {
      const targetChildren = selectedChildId === 'all'
        ? childList
        : childList.filter((c: any) => c.id === selectedChildId);

      const allAssessments = await Promise.all(
        targetChildren.map((child: any) =>
          guardianApi.portal.getChildAssessments(child.id, { limit: 100 })
            .then(items => items.map((a: any) => ({
              ...a,
              childName: `${child.first_name} ${child.last_name}`,
            })))
        )
      );

      return allAssessments
        .flat()
        .sort((a: any, b: any) => new Date(b.assessment_date).getTime() - new Date(a.assessment_date).getTime());
    },
    enabled: childList.length > 0,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="error">
        Failed to load assessments. Please try again later.
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <H1>All Assessments</H1>
        <Text className="mt-2 text-gray-600 dark:text-gray-400">
          View all published assessments for your children
        </Text>
      </div>

      {childList.length > 1 && (
        <Card className="p-4">
          <div className="flex items-center flex-wrap gap-3">
            <FunnelIcon className="w-5 h-5 text-gray-400 dark:text-gray-500" />
            <Text className="text-sm font-medium text-gray-700 dark:text-gray-300">Filter by child:</Text>
            <div className="flex flex-wrap gap-2">
              <Button
                variant={selectedChildId === 'all' ? 'primary' : 'outline'}
                size="sm"
                onClick={() => setSelectedChildId('all')}
              >
                All Children
              </Button>
              {childList.map((child: any) => (
                <Button
                  key={child.id}
                  variant={selectedChildId === child.id ? 'primary' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedChildId(child.id)}
                >
                  {child.first_name}
                </Button>
              ))}
            </div>
          </div>
        </Card>
      )}

      <div className="space-y-4">
        {assessments && assessments.length > 0 ? (
          assessments.map((assessment: any) => (
            <Card key={assessment.id} className="p-6">
              <div className="flex items-start justify-between mb-3">
                <div>
                  {childList.length > 1 && (
                    <Badge variant="primary" size="sm" className="mb-2">
                      {assessment.childName}
                    </Badge>
                  )}
                  <H3 className="text-base">
                    {assessment.learning_area_name || assessment.learning_area?.name || 'Learning Area'}
                  </H3>
                  <Text className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {assessment.learning_outcome?.description || ''}
                  </Text>
                </div>
                <Text className="text-sm text-gray-500 dark:text-gray-400">
                  {formatDate(assessment.assessment_date)}
                </Text>
              </div>

              {assessment.score !== null && assessment.score !== undefined && (
                <div className="mt-3">
                  <div className="flex items-center space-x-3">
                    <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                      <div
                        className="bg-green-500 dark:bg-green-400 h-2.5 rounded-full"
                        style={{
                          width: `${assessment.assessment_scale?.max_value
                            ? (assessment.score / assessment.assessment_scale.max_value) * 100
                            : 0}%`
                        }}
                      />
                    </div>
                    <Badge variant="success">
                      {assessment.score}/{assessment.assessment_scale?.max_value}
                    </Badge>
                  </div>
                  {assessment.assessment_scale?.name && (
                    <Text className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Scale: {assessment.assessment_scale.name}
                    </Text>
                  )}
                </div>
              )}

              {assessment.notes && (
                <Text className="mt-3 text-sm text-gray-700 dark:text-gray-300">
                  {assessment.notes}
                </Text>
              )}
            </Card>
          ))
        ) : (
          <Card className="p-8">
            <div className="text-center">
              <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
              <H3 className="mt-4 text-gray-900 dark:text-gray-100">No Assessments Yet</H3>
              <Text className="mt-2 text-gray-500 dark:text-gray-400">
                No published assessments available for your children.
              </Text>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};
